import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import APIKit from "../../config/APIKit";

export const fetchProducts = createAsyncThunk(
  "product/fetchProducts",
  async ({ categoryID, page = 1, limit = 20, search = '', subCategoryId = null }, { rejectWithValue }) => {
    try {
      const params = {
        page: page,
        limit,
        search
      };
      
      // Add sub_category_id to params if subcategory is selected
      if (subCategoryId) {
        params.sub_category_id = subCategoryId;
      }
      
      const response = await APIKit.get(`product/list/${categoryID}`, {
        params: params,
      });
      return response;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Products get failed");
      }
      return rejectWithValue("Products get failed");
    }
  }
);

export const searchProducts = createAsyncThunk(
  "product/searchProducts",
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`product/search`, {
        params: {
          q: text,
        },
      });
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Search failed");
      }
      return rejectWithValue("Search failed");
    }
  }
);

export const fetchOfferProducts = createAsyncThunk(
  "product/fetchOfferProducts",
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`offer/products`);
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Fetch offer failed");
      }
      return rejectWithValue("Fetch offer failed");
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState: {
    products: [],
    status: "idle",
    error: null,
    isSearchMode: false,
    isOffersMode: false,
    searchQuery: "",
    selectedCategoryId: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
      hasMore: false,
    },
    isLoadingMore: false,
  },
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.status = "idle";
      state.error = null;
      state.isSearchMode = false;
      state.isOffersMode = false;
      state.searchQuery = "";
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasMore: false,
      };
      state.isLoadingMore = false;
    },
    setSearchMode: (state, action) => {
      state.isSearchMode = action.payload;
    },
    clearSearchMode: (state) => {
      state.isSearchMode = false;
      state.isOffersMode = false;
      state.searchQuery = "";
    },
    setOffersMode: (state, action) => {
      state.isOffersMode = action.payload;
      state.isSearchMode = false;
    },
    setLoadingMore: (state, action) => {
      state.isLoadingMore = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state, action) => {
        const page = action.meta.arg.page || 1;
        if (page === 1) {
          state.status = "loading";
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
        state.isSearchMode = false;
        state.isOffersMode = false;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isLoadingMore = false;
        state.isSearchMode = false;
        state.isOffersMode = false;
        // Store the selected category ID from the request
        state.selectedCategoryId = action.meta.arg.categoryID;
        
        // Get page and limit from the request
        const page = action.meta.arg.page || 1;
        const limit = action.meta.arg.limit || 20;
        
        let newProducts = [];
        let totalCount = 0;
        
        // Handle both array and paginated response
        // APIKit returns response.data, so action.payload is already the data object
        if (action.payload.data && Array.isArray(action.payload.data.list)) {
          // API structure: { status, message, data: { list: [], totalCount: N } }
          newProducts = action.payload.data.list;
          totalCount = action.payload.data.totalCount || newProducts.length;
          
          // Calculate pagination metadata from totalCount
          const totalPages = Math.ceil(totalCount / limit);
          const currentLoadedCount = page === 1 ? newProducts.length : state.products.length + newProducts.length;
          
          state.pagination = {
            currentPage: page,
            totalPages: totalPages,
            totalItems: totalCount,
            itemsPerPage: limit,
            hasMore: currentLoadedCount < totalCount,
          };
        } else if (Array.isArray(action.payload)) {
          newProducts = action.payload;
          totalCount = action.payload.length;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          newProducts = action.payload.data;
          totalCount = action.payload.data.length;
        } else {
          newProducts = action.payload;
          totalCount = action.payload.length;
        }
        
        // Append products if it's a pagination request (page > 1), otherwise replace
        if (page === 1) {
          state.products = newProducts;
        } else {
          state.products = [...state.products, ...newProducts];
        }
        
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isLoadingMore = false;
        state.isSearchMode = false;
        state.isOffersMode = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Products get failed";
      })
      .addCase(searchProducts.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.isSearchMode = true;
        state.isOffersMode = false;
        state.selectedCategoryId = null; // Clear category selection in search mode
        state.searchQuery = action.meta.arg.text; // Store the search query
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isSearchMode = true;
        state.isOffersMode = false;
        // Handle both array and paginated response for search
        if (Array.isArray(action.payload)) {
          state.products = action.payload;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          state.products = action.payload.data;
        } else if (action.payload.data && Array.isArray(action.payload.data.list)) {
          state.products = action.payload.data.list;
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.currentPage || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.itemsPerPage || state.pagination.itemsPerPage,
            };
          }
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isSearchMode = true;
        state.isOffersMode = false;
        state.error =
          typeof action.payload === "string" ? action.payload : "Search failed";
      })
      .addCase(fetchOfferProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isOffersMode = true;
        state.isSearchMode = false;
      })
      .addCase(fetchOfferProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isOffersMode = true;
        state.isSearchMode = false;
        state.products = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(fetchOfferProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isOffersMode = true;
        state.isSearchMode = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "offer fetch get failed";
      });
  },
});

export const { clearProducts, setSearchMode, clearSearchMode, setOffersMode, setLoadingMore } =
  productSlice.actions;
export default productSlice.reducer;
